import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../services/apiService";
import "./css/Upload.css";

function Upload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseIdFromUrl = searchParams.get("courseId");
  const lessonIdFromUrl = searchParams.get("lessonId");
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [selectedLessonName, setSelectedLessonName] = useState("");
  const [file, setFile] = useState(null);
  const [uploadType, setUploadType] = useState("file");
  const [contentUrl, setContentUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState("idle");
  const [error, setError] = useState(null);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    const fetchCourseAndLesson = async () => {
      if (!courseIdFromUrl || !lessonIdFromUrl) {
        setError("Course ID and Lesson ID are required.");
        return;
      }

      try {
        // Fetch course name
        const courseData = await apiService.getCourseById(courseIdFromUrl);
        setSelectedCourseName(courseData.title);

        // Fetch lesson name
        const lessonData = await apiService.getLessonById(lessonIdFromUrl);
        setSelectedLessonName(lessonData.title);
      } catch (err) {
        console.error("Error fetching course/lesson:", err);
        setError("Failed to load course or lesson information.");
      }
    };

    fetchCourseAndLesson();
  }, [courseIdFromUrl, lessonIdFromUrl]);

  const uploadDocumentToAzure = async (file, lessonId) => {
    const maxRetries = 3;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Step 1: Generate upload URL from backend
        // Backend expects file_type to be one of: "lesson_doc", "syllabus", "lesson_plan"
        const backendFileType = "lesson_doc";
        const urlResult = await apiService.generateUploadUrl(
          file.name,
          backendFileType,
          lessonId
        );

        if (!urlResult.success) {
          throw new Error(urlResult.error);
        }

        const { upload_url, blob_name, container_name } = urlResult.data;

        // Step 2: Upload file directly to Azure Blob Storage with progress tracking
        // Track upload progress using XMLHttpRequest for better progress monitoring
        const uploadSuccess = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentCompleted = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(percentCompleted);
            }
          });

          xhr.addEventListener("load", () => {
            if (xhr.status === 201) {
              resolve(true);
            } else {
              reject(
                new Error(`Azure upload failed with status ${xhr.status}`)
              );
            }
          });

          xhr.addEventListener("error", () =>
            reject(new Error("Network error during upload"))
          );

          xhr.open("PUT", upload_url);
          xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
          xhr.setRequestHeader("Content-Type", "application/octet-stream");
          xhr.send(file);
        });

        if (!uploadSuccess) {
          throw new Error("Azure upload failed");
        }

        // Return metadata needed for confirmation
        return {
          blob_name,
          container_name,
          success: true,
        };
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error.message);

        if (attempt === maxRetries) {
          throw new Error(
            `Upload failed after ${maxRetries} attempts: ${error.message}`
          );
        }

        // Wait before retrying (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
  };

  const getFileTypeFromExtension = (file) => {
    if (!file) return null;

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const videoExtensions = ["mp4", "mov", "mkv", "mp3", "wav"];
    if (
      videoExtensions.some((ext) => fileName.endsWith(`.${ext}`)) ||
      fileType.startsWith("video/") ||
      fileType.startsWith("audio/")
    ) {
      return "video";
    }

    if (fileName.endsWith(".pdf") || fileType === "application/pdf") {
      return "pdf";
    }

    if (fileName.endsWith(".txt") || fileType === "text/plain") {
      return "text";
    }

    return null;
  };

  const validateFile = (file) => {
    setFileError(null);

    if (!file) {
      setFileError("Please select a file");
      return false;
    }

    const fileType = getFileTypeFromExtension(file);

    if (!fileType) {
      setFileError(
        "Unsupported file type. Please select MP4, PDF, or TXT files."
      );
      return false;
    }

    if (!["video", "pdf", "text"].includes(fileType)) {
      setFileError(
        "Unsupported file type. Please select a video (MP4, MOV, MKV), audio (MP3, WAV), PDF, or TXT file."
      );
      return false;
    }

    // File size validation
    const fileSizeInMB = file.size / (1024 * 1024); // Convert bytes to MB

    if (fileType === "video") {
      // Video/Audio files: max 30 MB
      if (fileSizeInMB > 30) {
        setFileError(
          "Video/Audio file size must be less than 30 MB. Current size: " +
            fileSizeInMB.toFixed(2) +
            " MB"
        );
        return false;
      }
    } else if (fileType === "pdf" || fileType === "text") {
      // PDF/Text files: max 5 MB
      if (fileSizeInMB > 5) {
        setFileError(
          "PDF/Text file size must be less than 5 MB. Current size: " +
            fileSizeInMB.toFixed(2) +
            " MB"
        );
        return false;
      }
    }

    return true;
  };

  const handleFileChange = (e) => {
    setFileError(null);
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      validateFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!courseIdFromUrl || !lessonIdFromUrl) {
      setError("Course ID and Lesson ID are required.");
      return;
    }

    if (uploadType === "file" && !file) {
      setError("Please select a file to upload.");
      return;
    }

    if (uploadType === "url" && !contentUrl) {
      setError("Please provide a content URL.");
      return;
    }

    if (uploadType === "file" && file && !validateFile(file)) {
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const targetLessonId = lessonIdFromUrl;
      let newSerialNumber = 1;

      setUploadStage("fetching");

      const existingLesson = await apiService.getLessonById(targetLessonId);
      if (existingLesson && existingLesson.doc_resources) {
        const maxSerialNumber = Math.max(
          0,
          ...existingLesson.doc_resources.map((r) => r.serialNumber || 0)
        );
        newSerialNumber = maxSerialNumber + 1;
      }
      console.log(
        `Uploading to lesson ${targetLessonId} with new serial number ${newSerialNumber}`
      );

      if (uploadType === "file" && file) {
        setUploadStage("uploading");
        const fileType = getFileTypeFromExtension(file);
        console.log("Detected file type for upload:", fileType);

        if (fileType === "video" || fileType === "pdf" || fileType === "text") {
          // Upload to Azure and get blob metadata
          const uploadResult = await uploadDocumentToAzure(
            file,
            targetLessonId
          );

          if (!uploadResult || !uploadResult.success) {
            throw new Error("Azure upload failed");
          }

          console.log("Azure upload successful:", uploadResult);

          // fileType is the content type: 'video', 'pdf', 'text'
          // category should be 'lesson' for lesson documents
          const category = "lesson";

          // Step 3: Confirm upload with backend to save in database
          const confirmResult = await apiService.confirmFileUpload(
            uploadResult.blob_name,
            file.name,
            fileType,
            targetLessonId,
            category,
            newSerialNumber,
            uploadResult.container_name
          );

          if (!confirmResult.success) {
            throw new Error(confirmResult.error);
          }

          console.log("Document upload confirmed:", confirmResult.data);
        } else {
          throw new Error(`Unsupported file type: ${fileType}`);
        }
        setUploadProgress(100);
      } else if (uploadType === "url") {
        console.log("Content URL provided:", contentUrl);
        // For URL-based content, you might want to update the lesson with the URL
        // This depends on your backend API requirements
      }

      alert(`Successfully uploaded content.`);

      // Redirect to the lesson resources page
      navigate(`/lessons/${lessonIdFromUrl}/resources`);
    } catch (err) {
      console.error("Error uploading content:", err);
      if (err.response) {
        console.error("Backend error:", err.response.data);
        setError(
          "Failed to upload content: " + JSON.stringify(err.response.data)
        );
      } else {
        setError(err.message || "Failed to upload content. Please try again.");
      }
    } finally {
      setLoading(false);
      setUploadStage("idle");
    }
  };

  if (!courseIdFromUrl || !lessonIdFromUrl) {
    return (
      <div className="upload-container">
        <h1>Upload Content</h1>
        <div className="upload-error">
          Course ID and Lesson ID are required. Please navigate from a lesson page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="btn btn-secondary"
        onClick={() => navigate(`/lessons/${lessonIdFromUrl}/resources`)}
      >
        ← Back to Lesson
      </button>
      <div className="upload-container">
        <h1 style={{ marginTop: "1rem" }}>
          Upload Content
        </h1>
        
        {selectedCourseName && (
          <p style={{ color: "var(--text-light)", marginTop: "0.5rem", marginBottom: "1rem" }}>
            Course: <strong>{selectedCourseName}</strong>
            {selectedLessonName && (
              <> | Lesson: <strong>{selectedLessonName}</strong></>
            )}
          </p>
        )}

        {error && <div className="upload-error">{error}</div>}

        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p className="loading-text">
              {uploadStage === "uploading"
                ? "Uploading file to Azure..."
                : uploadStage === "fetching"
                ? "Getting lesson details..."
                : "Processing..."}
            </p>
            {uploadStage === "uploading" && (
              <div className="progress-container">
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="progress-text">{uploadProgress}% complete</p>
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Upload Method</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="file"
                checked={uploadType === "file"}
                onChange={() => setUploadType("file")}
                disabled={loading}
              />
              File Upload
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="url"
                checked={uploadType === "url"}
                onChange={() => setUploadType("url")}
                disabled={loading}
              />
              URL Input
            </label>
          </div>
        </div>

        {uploadType === "file" ? (
          <div className="form-group">
            <label className="form-label">Upload File</label>
            <input
              className="form-control"
              type="file"
              disabled={loading}
              onChange={handleFileChange}
              accept="video/mp4,video/quicktime,video/x-matroska,audio/mpeg,audio/wav,text/plain,application/pdf"
            />
            {fileError && <div className="file-error-text">{fileError}</div>}
            <div className="file-hint-text">
              Accepted formats: MP4, MOV, MKV, MP3, WAV, PDF, or TXT files
            </div>
          </div>
        ) : (
          <div className="form-group">
            <label className="form-label">Content URL</label>
            <input
              className="form-control"
              type="url"
              value={contentUrl}
              disabled={loading}
              onChange={(e) => setContentUrl(e.target.value)}
              placeholder="https://example.com/content"
            />
          </div>
        )}

        <button
          className="upload-button"
          onClick={handleUpload}
          disabled={loading}
        >
          {loading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>
  );
}

export default Upload;
